import React, { useEffect, useState, useRef, useMemo } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
} from "chart.js";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";

import ToolTip from "./ToolTip";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip);

function WLMLineChart({ resdata }) {
  const [tooltipModel, setTooltipModel] = useState({});
  const [showTooltip, setShowTooltip] = useState(false);
  const [position, setPosition] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const menuRef = useRef();
  const chartRef = useRef();
  const chartContainerRef = useRef(null);

  const weightLineStyle = {
    fill: false,
    lineTension: 0.3,
    backgroundColor: "#FFA500",
    borderColor: "#FFA500",
    borderCapStyle: "round",
    borderJoinStyle: "round",
    pointBorderColor: "#FFA500",
    pointBackgroundColor: "#fff",
    pointBorderWidth: 1,
    pointHoverRadius: 5,
    pointHoverBackgroundColor: "#FFA500",
    pointHoverBorderColor: "#fff",
    pointHoverBorderWidth: 3,
    pointRadius: 3,
    pointHitRadius: 5,
  };


useEffect(() => {
  const handleClickOutside = (e) => {
    if (menuRef.current && !menuRef.current.contains(e.target)) {
      setModalOpen(false);
    }
  };

  document.addEventListener("mousedown", handleClickOutside);
  return () => document.removeEventListener("mousedown", handleClickOutside);
}, []);
  const formattedData = useMemo(() => {
    if (!resdata) return [];
    const combined = (resdata.datelabel || []).map((label, i) => ({
      datetime: dayjs(label, "MM-DD-YYYY hh:mm A"),
      weight: resdata.weight[i],
      weightUnits: resdata.weightunits?.[i] || "",
    }));
    combined.sort((a, b) => a.datetime - b.datetime);
    return combined.map((item) => ({
      date: item.datetime.format("MM-DD-YYYY hh:mm A"),
      weight: item.weight,
      weightUnits: item.weightUnits,
    }));
  }, [resdata]);

  const [chartData, setChartData] = useState({ labels: [], datasets: [] });

  useEffect(() => {
    setChartData({
      labels: formattedData.map((item) => item.date),
      datasets: [
        {
          ...weightLineStyle,
          data: formattedData.map((item) => item.weight),
          label: "Weight",
        },
      ],
    });
  }, [formattedData]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        ticks: { stepSize: 5 },
        grid: { drawTicks: false },
      },
      x: { grid: { display: false } },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: false,
        mode: "index",
        intersect: false,
        external: (context) => {
          const tooltip = context.tooltip;
          if (tooltip.opacity === 0) {
            setShowTooltip(false);
            return;
          }
          setTooltipModel(tooltip);
          setPosition(context.chart.canvas.getBoundingClientRect());
          setShowTooltip(true);
        },
      },
    },
  };

  // Download handlers
  const handleDownload = (type) => {
    if (!chartRef.current) return;
    const canvas = chartRef.current.canvas;
    const tempCanvas = document.createElement("canvas");
    tempCanvas.width = canvas.width;
    tempCanvas.height = canvas.height;
    const ctx = tempCanvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(canvas, 0, 0);
    const link = document.createElement("a");
    link.download = `chart.${type}`;
    link.href = tempCanvas.toDataURL(`image/${type}`);
    link.click();
    setModalOpen(false);
  };

  const handlePDFDownload = () => {
    if (!chartRef.current) return;
    const canvas = chartRef.current.canvas;
    const pdf = new jsPDF({
      orientation: canvas.width > canvas.height ? "landscape" : "portrait",
      unit: "pt",
      format: [canvas.width, canvas.height],
    });
    pdf.addImage(canvas.toDataURL("image/png"), "PNG", 0, 0, canvas.width, canvas.height);
    pdf.save("chart.pdf");
    setModalOpen(false);
  };

  const handleSVGDownload = () => {
    if (!chartRef.current) return;
    const canvas = chartRef.current.canvas;
    const imgData = canvas.toDataURL("image/png");
    const svgContent = `
      <svg xmlns="http://www.w3.org/2000/svg" width="${canvas.width}" height="${canvas.height}">
        <image href="${imgData}" height="${canvas.height}" width="${canvas.width}" />
      </svg>
    `.trim();
    const blob = new Blob([svgContent], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "chart.svg";
    link.click();
    setModalOpen(false);
  };

  const handleCSVDownload = () => {
    const csv = ["Date,Weight,WeightUnits"];
    formattedData.forEach((row) => {
      csv.push([row.date, row.weight, row.weightUnits].join(","));
    });
    const blob = new Blob([csv.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "chart-data.csv";
    link.click();
    setModalOpen(false);
  };

  const handleXLSDownload = () => {
    const wsData = [["Date", "Weight", "WeightUnits"], ...formattedData.map((row) => [row.date, row.weight, row.weightUnits])];
    const worksheet = XLSX.utils.aoa_to_sheet(wsData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Chart Data");
    XLSX.writeFile(workbook, "chart-data.xlsx");
    setModalOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setModalOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div style={{ width: "100%", height: "300px", position: "relative" }} ref={chartContainerRef}>
      <div className="flex justify-end px-4 pt-2" ref={menuRef}>
        <button className="text-gray-600 hover:text-black" onClick={() => setModalOpen(true)}>☰</button>
      </div>

      <Line ref={chartRef} data={chartData} options={options} />

      {tooltipModel.dataPoints &&
        tooltipModel.dataPoints.map((tooltip, index) => (
          <ToolTip
            key={index}
            data={tooltip}
            isShow={showTooltip}
            position={position}
            color={tooltipModel.labelColors[index].borderColor}
          />
        ))}

      {modalOpen && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center">
          <div className="bg-white text-sm rounded shadow-lg w-[90%] max-w-[512px]" ref={menuRef}>
            <div className="flex flex-wrap justify-start px-4 py-2 gap-4 text-blue-600">
              <button onClick={() => handleDownload("png")} className="hover:underline">Download PNG</button>
              <button onClick={() => handleDownload("jpeg")} className="hover:underline">Download JPEG</button>
              <button onClick={handlePDFDownload} className="hover:underline">Download PDF</button>
              <button onClick={handleSVGDownload} className="hover:underline">Download SVG</button>
              <button onClick={handleCSVDownload} className="hover:underline">Download CSV</button>
              <button onClick={handleXLSDownload} className="hover:underline">Download XLS</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default WLMLineChart;
